import axios from 'axios';
import { installDeviceKey } from './deviceKey';

function getCookie(name) {
    let re = new RegExp(name + "=([^;]+)"),
        value = re.exec(document.cookie);
    return (value != null) ? unescape(value[1]) : null;
}

axios.defaults.withCredentials = true;
axios.defaults.headers.common['X-CSRFToken'] = getCookie('csrftoken');
// Device-bound sessions: send the stored key on same-origin calls, and take
// a freshly issued one from any response that carries it. Installed on the
// default instance, which is the app's only network stack — `services.js` is
// the single module that imports axios, and nothing here uses fetch/XHR
// directly, so every API call goes through these interceptors.
installDeviceKey(axios);

function normalizeError(error) {
    const response = error?.response || {};
    return Promise.reject({
        body: response.data || {},
        bodyText: typeof response.data === 'string' ? response.data : JSON.stringify(response.data || {}),
        status: response.status,
        statusText: response.statusText || error?.message || 'Request failed',
    });
}

function resolveUrl(url, payload = {}) {
    const params = { ...payload };
    let finalUrl = url;

    for (const [key, value] of Object.entries(payload)) {
        const encodedValue = encodeURIComponent(value);
        if (finalUrl.includes(`{/${key}}`)) {
            finalUrl = finalUrl.replace(`{/${key}}`, value === undefined || value === null || value === '' ? '' : `/${encodedValue}`);
            delete params[key];
        }
        if (finalUrl.includes(`{${key}}`)) {
            finalUrl = finalUrl.replace(`{${key}}`, encodedValue);
            delete params[key];
        }
    }

    finalUrl = finalUrl.replace(/\{\/[^}]+\}/g, '').replace(/\{[^}]+\}/g, '');
    return { finalUrl, params };
}

export function createResource(url) {
    return {
        get(params = {}) {
            const resolved = resolveUrl(url, params);
            return axios.get(resolved.finalUrl, { params: resolved.params })
                .then(r => ({ body: r.data }))
                .catch(normalizeError);
        },
        save(data = {}, params = {}) {
            const resolved = resolveUrl(url, params);
            return axios.post(resolved.finalUrl, data)
                .then(r => ({ body: r.data }))
                .catch(normalizeError);
        },
        update(data = {}, params = {}) {
            const resolved = resolveUrl(url, params);
            return axios.put(resolved.finalUrl, data)
                .then(r => ({ body: r.data }))
                .catch(normalizeError);
        },
        delete(params = {}) {
            const resolved = resolveUrl(url, params);
            return axios.delete(resolved.finalUrl, { params: resolved.params })
                .then(r => ({ body: r.data }))
                .catch(normalizeError);
        },
        remove(params = {}) {
            return this.delete(params);
        }
    };
}

function cached(resource) {
    let originalGet = resource.get;
    resource._cache = null;
    resource.get = function(params) {
        if (!resource._cache) {
            resource._cache = originalGet(params);
        }
        return resource._cache;
    };
    return resource;
}

const capiUrl = '/api/';

export const services = {
    Login: () => createResource(capiUrl + 'users/login'),
    Register: () => createResource(capiUrl + 'users/register'),
    Context: () => createResource(capiUrl + 'users/context'),
    Logout: () => createResource(capiUrl + 'users/logout'),
    Forgot: () => createResource(capiUrl + 'users/forgot'),
    Reset: () => createResource(capiUrl + 'users/reset'),
    Password: () => createResource(capiUrl + 'users/password'),
    Tfa: () => createResource(capiUrl + 'users/tfa'),
    TfaSetup: () => createResource(capiUrl + 'users/tfa/setup'),
    TfaEnable: () => createResource(capiUrl + 'users/tfa/enable'),
    TfaDisable: () => createResource(capiUrl + 'users/tfa/disable'),
    TfaRecoveryCodes: () => createResource(capiUrl + 'users/tfa/recovery-codes'),
    TfaVerify: () => createResource(capiUrl + 'users/tfa/verify'),
    Confirm: () => createResource(capiUrl + 'users/confirm{/uid}{/token}'),
    Unsubscribe: () => createResource(capiUrl + 'users/unsubscribe{/uid}{/token}{/code}'),
    Plan: () => createResource(capiUrl + 'users/plan'),
    PlanLowest: () => cached(createResource(capiUrl + 'users/plan/lowest')),
    CurrencyRate: () => cached(createResource(capiUrl + 'users/currency/RUB')),
    Data: () => cached(createResource(capiUrl + 'dash/data')),
    Stats: () => createResource(capiUrl + 'dash/stats'),
    Token: () => createResource(capiUrl + 'users/token'),
    PromoCode: () => createResource(capiUrl + 'users/promocode'),
    APIKey: () => createResource(capiUrl + 'users/apikey'),
    Transaction: () => createResource(capiUrl + 'users/transaction'),
    Payment: () => createResource(capiUrl + 'users/payment'),
    PaymentSystem: () => createResource(capiUrl + 'users/payment/system'),
    Invoice: () => createResource(capiUrl + 'users/invoice/{id}'),
    Review: () => createResource(capiUrl + 'users/review'),
    ReviewAggregate: () => cached(createResource(capiUrl + 'users/review/aggregate')),
    Payout: () => createResource(capiUrl + 'users/payout'),
    TgLogin: () => createResource(capiUrl + 'users/tg-login'),
    TgComplete: () => createResource(capiUrl + 'users/tg-complete'),
    BlogPosts: () => createResource(capiUrl + 'blog/posts'),
    BlogPost: () => createResource(capiUrl + 'blog/posts/{slug}'),
    Releases: () => createResource(capiUrl + 'releases/'),
    Status: () => cached(createResource(capiUrl + 'status')),
    SiteContacts: () => cached(createResource(capiUrl + 'site-contacts')),
    SiteFooter: () => cached(createResource(capiUrl + 'site-footer')),
    HealthCheck: (proto, domain) => createResource(`${proto}://api.${domain}/sys/healthcheck`)
};

export default services;
